import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";

const MAX_POLLS = 12;

export default function PaymentReturn() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const { reloadUser } = useApp();
  const isCancel = loc.pathname.includes("/cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");
  const polls = useRef(0);

  useEffect(() => {
    if (isCancel || !sessionId) {
      if (!sessionId && !isCancel) setState("error");
      return;
    }
    let stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await reloadUser();
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) { /* transient */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [sessionId, isCancel]);

  return (
    <Shell>
      <div className="px-6 pt-28 text-center" data-testid="payment-return">
        {state === "polling" && (
          <>
            <Loader2 size={34} strokeWidth={1.5} className="mx-auto text-[#C9A227] animate-spin" />
            <p className="mt-5 text-[#6B6B6B] font-mono-ss text-sm">Confirming your subscription…</p>
          </>
        )}

        {state === "success" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-success">
            <CheckCircle2 size={44} strokeWidth={1.4} className="mx-auto text-[#2f6b45]" />
            <h1 className="mt-5 font-serif-display text-4xl">You're on Plus.</h1>
            <p className="mt-3 text-[15px] text-[#474747] leading-relaxed">
              Your Pattern Review and cross-device history are unlocked. Thank you for supporting
              Signal State ID.
            </p>
            <button data-testid="success-open-pattern" onClick={() => nav("/pattern")}
              className="mt-7 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus">
              Open my Pattern Review
            </button>
            <button data-testid="success-navigate" onClick={() => nav("/navigate")}
              className="mt-3 w-full border border-[#1A1A1A] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus">
              Do a check-in
            </button>
          </motion.div>
        )}

        {state === "cancelled" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-cancelled">
            <XCircle size={40} strokeWidth={1.4} className="mx-auto text-[#6B6B6B]" />
            <h1 className="mt-5 font-serif-display text-3xl">Checkout cancelled.</h1>
            <p className="mt-3 text-[15px] text-[#474747]">No charge was made. You can upgrade whenever you're ready.</p>
            <button data-testid="cancel-back-plus" onClick={() => nav("/plus")}
              className="mt-7 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus">
              Back to Plus
            </button>
            <button data-testid="cancel-home" onClick={() => nav("/navigate")}
              className="mt-3 w-full text-[14px] text-[#474747] hover:text-[#1A1A1A] ss-focus py-2">
              Continue for free
            </button>
          </motion.div>
        )}

        {(state === "error" || state === "timeout") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="payment-error">
            <h1 className="font-serif-display text-3xl">
              {state === "timeout" ? "Still processing…" : "Something went wrong."}
            </h1>
            <p className="mt-3 text-[15px] text-[#474747]">
              {state === "timeout"
                ? "Your payment may still be completing. Check Settings in a moment, or contact support if it doesn't appear."
                : "We couldn't confirm this payment. No need to worry — you can try again."}
            </p>
            <button data-testid="error-back-plus" onClick={() => nav("/plus")}
              className="mt-7 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus">
              Back to Plus
            </button>
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
